/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useCallback, useState} from 'react';
import {useAgentsAPI} from './useAgentsAPI';
import {type APIParams, METHODS} from '../../../hooks/abstractions/useTrusstedAgent';
import {AGENTS_API_EVENT_NAMES} from '../../../types/agents-api';

const customLabels: Record<string, string> = {
  http: 'HTTP',
};

export type AgentAbilityType = {
  name: string;
  label: string;
  userVariables: {name: string; label: string; default: string}[];
};

export const useGetAgentAbilityTypes = () => {
  const agentsAPI = useAgentsAPI();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abilityTypes, setAbilityTypes] = useState<AgentAbilityType[]>([]);

  const get = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await agentsAPI({
        eventName: AGENTS_API_EVENT_NAMES.GetFunctionTypes,
        methodName: METHODS.GET,
      } as APIParams);

      const functionTypes = result.functionTypes;
      const mappedAbilityTypes: AgentAbilityType[] = functionTypes.map((type: any) => {
        const label = customLabels[type.name] || type.name.charAt(0).toUpperCase() + type.name.slice(1);
        const userVariables = (type.userVariables || []).map((variable: any) => ({
          ...variable,
          default: variable.default || '',
        }));
        return {
          ...type,
          label,
          userVariables,
        };
      });

      setAbilityTypes(mappedAbilityTypes);
      setIsLoading(false);
      return mappedAbilityTypes;
    } catch (err) {
      setIsLoading(false);
      setError('Failed to get agent ability types');
      return Promise.reject(err);
    }
  }, [agentsAPI]);

  return {
    abilityTypes,
    isLoading,
    error,
    get,
  };
};
